import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import { AddCircleOutlineOutlined as AddIcon } from "@mui/icons-material";
import PropTypes from "prop-types";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";

import CostEntryForm from "./CostEntryForm.jsx";
import CostEntryRow from "./CostEntryRow.jsx";
import { uploadClaimAttachment } from "../../Form/controls/FilePicker/useClaimAttachmentFiles.js";

/**
 * Main component for managing claim costs.
 * All changes are managed via value/onChange props (form state).
 * Changes are only persisted when the main form is saved.
 *
 * File picker fields store real server IDs immediately upon upload.
 * At submission the parent should call `prepareForSubmission(costs)` to enrich
 * pending attachment references with both `id` (server) and `uuid` (client)
 * for backend ownership verification.
 */
const ClaimCostsEditor = ({
  disabled,
  value = [],
  onChange,
  onFormChange,
  onFormOpenChange,
  amountField,
  claimId,
  claimAttachmentFiles = [],
  sessionAttachmentFiles = [],
  addSessionAttachmentFile,
  updateSessionAttachmentFileDescription,
  getUuidForServerId,
  clearSessionAttachments,
  costTypes,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tempIdCounter = useRef(0);

  // Bind upload to the current claimId (null → orphan attachment for new claims).
  const uploadFn = useCallback(
    (file, uuid, description) =>
      uploadClaimAttachment(claimId, file, uuid, description),
    [claimId]
  );

  // Generate a temporary ID for new costs
  const generateTempId = () => {
    tempIdCounter.current += 1;
    return `temp_${Date.now()}_${tempIdCounter.current}`;
  };

  // Calculate total from current costs
  const calculateTotal = useCallback((costList) => {
    return costList.reduce(
      (sum, cost) => sum + (parseFloat(cost.amount) || 0),
      0
    );
  }, []);

  const totalAmount = calculateTotal(value);

  const isFormOpen = showForm || !!editingCost;

  useEffect(() => {
    if (onFormOpenChange) {
      onFormOpenChange(isFormOpen);
    }
  }, [isFormOpen]);

  // Sync total to gesamtschadenkosten form field
  useEffect(() => {
    // Trigger onFormChange for gesamtschadenkosten field
    if (onFormChange) {
      onFormChange({
        target: { name: "gesamtschadenkosten", value: totalAmount },
      });
    }
  }, [totalAmount]);

  // Every change to the rows overwrites the amount of the Sparte step the editor sits in.
  const changeCosts = (updatedCosts) => {
    onChange(updatedCosts);
    if (amountField && onFormChange) {
      onFormChange({
        target: { name: amountField, value: calculateTotal(updatedCosts) },
      });
    }
  };

  const handleAddClick = () => {
    setEditingCost(null);
    setShowForm(true);
  };

  const handleEditClick = (cost) => {
    setEditingCost(cost);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCost(null);
  };

  const handleSubmit = (formData) => {
    if (editingCost?.id) {
      // update existing cost
      const updatedCosts = value.map((cost) =>
        cost.id === editingCost.id ? { ...formData, id: editingCost.id } : cost
      );
      changeCosts(updatedCosts);
    } else {
      // add new cost with temporary ID
      const newCost = {
        ...formData,
        id: generateTempId(),
      };
      changeCosts([...value, newCost]);
    }

    setShowForm(false);
    setEditingCost(null);
  };

  const handleDeleteClick = (cost) => {
    setDeleteConfirm(cost);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) {
      return;
    }

    const filteredCosts = value.filter((cost) => cost.id !== deleteConfirm.id);
    changeCosts(filteredCosts);
    setDeleteConfirm(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  return (
    <Paper elevation={0} sx={{}}>
      {value.length === 0 && !showForm ? (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          Noch keine Kosten erfasst
        </Typography>
      ) : (
        <Box>
          {value.map((cost) =>
            editingCost?.id === cost.id ? (
              <Paper
                variant="outlined"
                key={cost.id}
                sx={{
                  p: 2,
                  mb: 1,
                  borderColor: "primary.main",
                  boxShadow: 3,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Kostenposition bearbeiten
                </Typography>
                <CostEntryForm
                  initialData={editingCost}
                  onSubmit={handleSubmit}
                  onCancel={handleCancelForm}
                  claimAttachmentFiles={claimAttachmentFiles}
                  sessionAttachmentFiles={sessionAttachmentFiles}
                  addSessionAttachmentFile={(file) =>
                    addSessionAttachmentFile(file, uploadFn)
                  }
                  updateSessionAttachmentFileDescription={
                    updateSessionAttachmentFileDescription
                  }
                  costTypes={costTypes}
                />
              </Paper>
            ) : (
              <Box
                key={cost.id}
                sx={{
                  opacity: isFormOpen ? 0.3 : 1,
                  pointerEvents: isFormOpen ? "none" : "auto",
                  transition: "opacity 0.15s",
                }}
              >
                <CostEntryRow
                  cost={cost}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  disabled={disabled || showForm}
                />
              </Box>
            )
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 2,
          mt: 4,
        }}
      >
        {!disabled && !showForm && !editingCost && (
          <Button
            startIcon={<AddIcon />}
            size="medium"
            onClick={handleAddClick}
          >
            Hinzufügen
          </Button>
        )}
      </Box>

      <Collapse in={showForm && !editingCost}>
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, borderColor: "primary.main", boxShadow: 3 }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Neue Kostenposition
          </Typography>
          <CostEntryForm
            initialData={editingCost}
            onSubmit={handleSubmit}
            onCancel={handleCancelForm}
            claimAttachmentFiles={claimAttachmentFiles}
            sessionAttachmentFiles={sessionAttachmentFiles}
            addSessionAttachmentFile={(file) =>
              addSessionAttachmentFile(file, uploadFn)
            }
            updateSessionAttachmentFileDescription={
              updateSessionAttachmentFileDescription
            }
            costTypes={costTypes}
          />
        </Paper>
      </Collapse>

      {value.length > 0 && (
        <Box
          sx={{
            opacity: isFormOpen ? 0.3 : 1,
            pointerEvents: isFormOpen ? "none" : "auto",
            transition: "opacity 0.15s",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1">Gesamtkosten</Typography>
            <Typography variant="h6" color="primary">
              {formatCurrency(totalAmount)}
            </Typography>
          </Box>
        </Box>
      )}

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Kostenposition löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll diese Kostenposition wirklich gelöscht werden? Diese Aktion
            kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" onClick={() => setDeleteConfirm(null)}>
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm}>Löschen</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

ClaimCostsEditor.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.array,
  onChange: PropTypes.func,
  onFormChange: PropTypes.func,
  onFormOpenChange: PropTypes.func,
  amountField: PropTypes.string,
  claimId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  claimAttachmentFiles: PropTypes.array,
  sessionAttachmentFiles: PropTypes.array,
  addSessionAttachmentFile: PropTypes.func.isRequired,
  updateSessionAttachmentFileDescription: PropTypes.func,
  getUuidForServerId: PropTypes.func.isRequired,
  clearSessionAttachments: PropTypes.func.isRequired,
  costTypes: PropTypes.arrayOf(PropTypes.string),
};

/**
 * Prepare costs array for submission by enriching pending attachment
 * references with both server ID and client UUID for backend verification.
 *
 * @param {Array} costs - the current costs array
 * @param {(serverId: number|string) => string|null} getUuidForServerId
 * @returns {Array} costs ready for submission
 */
export function prepareForSubmission(costs, getUuidForServerId) {
  return (costs ?? []).map((cost) => {
    if (!cost.data) return cost;

    const preparedData = { ...cost.data };

    for (const key of Object.keys(preparedData)) {
      if (!key.endsWith("_attachment")) continue;
      const val = preparedData[key];
      if (!val) continue;

      // Backend may send hydrated attachment objects {id, filename, url}
      // Extract the actual ID - handle both plain IDs and hydrated objects
      const serverId = typeof val === "object" ? val.id : val;
      const used_in = typeof val === "object" ? (val.used_in ?? []) : [];
      const uuid = getUuidForServerId(serverId);
      preparedData[key] = uuid
        ? { id: serverId, uuid, used_in }
        : { id: serverId, used_in };
    }

    return { ...cost, data: preparedData };
  });
}

export default ClaimCostsEditor;
